/**
 * Jump targets for the combined-result chips.
 *
 * The chips name a score ("Employer: 97") that is explained in full somewhere
 * further down the same tab, and a reviewer's next move after reading one is
 * always to go find that explanation. Scrolling for it by eye is the part worth
 * removing.
 *
 * Both halves live here so the chip and its target cannot drift apart: one map
 * from the combined score's domain key to the element that explains it.
 */

/**
 * Domain key (as emitted by `AGENT1_COMBINED.breakdown[].domain`) → DOM id.
 *
 * Note `bureau_credit` → `decision_engine`: the backend names the domain after
 * the bureau and the source card after the credit engine. This is the only place
 * the two vocabularies have to meet.
 */
const DOMAIN_ANCHOR: Record<string, string> = {
  identity: 'loan-review-anchor-identity',
  income: 'loan-review-anchor-income',
  employment: 'loan-review-anchor-employer',
  bureau_credit: 'loan-review-anchor-decision_engine',
  cross_check: 'loan-review-anchor-cross_check',
  // Adjusters, not Agent-1 domains — reached from the final-score table rather
  // than the domain chips. `external` has no source card; the Agent 3 findings
  // panel is its explanation. `history` has no target yet (Agent 2 is a
  // placeholder), so its row stays plain until it does.
  external: 'loan-review-anchor-external',
};

/** DOM id for a source card, given the key the backend emits. */
export function sourceCardAnchorId(cardKey: string): string {
  return `loan-review-anchor-${cardKey}`;
}

export const EXTERNAL_ANCHOR_ID = DOMAIN_ANCHOR.external;

/** The DOM id explaining a domain's score, or null when the domain has no target. */
export function anchorIdFor(domain: string): string | null {
  return DOMAIN_ANCHOR[domain] ?? null;
}

/** Every domain/adjuster with a jump target. Exported so a test catches drift. */
export const ANCHORED_DOMAINS: readonly string[] = Object.keys(DOMAIN_ANCHOR);

/**
 * Which anchors this application actually renders.
 *
 * Derived from the view model, deliberately not from the DOM. The chips render in
 * the same commit as the cards they point at, so a `getElementById` check runs
 * before those cards exist and would report every target missing on first paint.
 *
 * A source card is omitted when its gate never ran, and the Agent 3 panel only
 * appears when there is a subscore — so this cannot just be the full domain list
 * either. A chip that offers to scroll somewhere nothing is rendered is worse than
 * a chip that stays plain.
 */
export function availableAnchors(vm: {
  sourceCards: Array<{ key: string }>;
  agent3?: { externalSubscore?: unknown } | null;
}): ReadonlySet<string> {
  const ids = new Set<string>(vm.sourceCards.map((c) => sourceCardAnchorId(c.key)));
  if (vm.agent3?.externalSubscore) ids.add(EXTERNAL_ANCHOR_ID);
  return ids;
}

/** True when this domain has somewhere to jump to — drives whether a chip is a button. */
export function hasAnchor(domain: string, available: ReadonlySet<string>): boolean {
  const id = anchorIdFor(domain);
  return id !== null && available.has(id);
}

/**
 * Scroll a domain's explanation into view and flash it.
 *
 * The flash matters: on a long tab a smooth scroll can end with the reader unsure
 * which of several similar cards they were sent to. Highlighting the destination
 * for a moment answers that without them having to re-read headings.
 */
export function scrollToDomain(domain: string): void {
  const id = anchorIdFor(domain);
  if (!id) return;
  const el = document.getElementById(id);
  if (!el) return;

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

  // A collapsed <details> target (the Agent 3 panel sits inside one) would scroll
  // to a closed summary and appear to do nothing. Open it on the way.
  const details = el.closest('details');
  if (details && !details.open) details.open = true;

  el.classList.remove(FLASH_CLASS);
  // Force a reflow so re-adding the class restarts the animation when the same
  // chip is clicked twice.
  void el.offsetWidth;
  el.classList.add(FLASH_CLASS);
  window.setTimeout(() => el.classList.remove(FLASH_CLASS), FLASH_MS);
}

export const FLASH_CLASS = 'loan-review-jump-flash';
const FLASH_MS = 1400;
